import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  ArrowLeft,
  AlertCircle,
  Copy,
  Download,
  FileText,
  Search,
  ExternalLink,
  Send,
  Trash2,
  BadgeCheck,
  XCircle,
  Hourglass,
  Zap,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Spinner,
  Modal,
  Select,
} from '../components/ui';
import { blogsApi } from '../lib/api';
import { formatDateTime, estimateReadingTime } from '../lib/utils';
import { getStatusConfig } from '../lib/statusConfig';
import toast from 'react-hot-toast';
import type { MediumPublishStatus } from '../types';

export const BlogDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'content' | 'seo'>('content');

  const { data: job, isLoading, error } = useQuery({
    queryKey: ['job', id],
    queryFn: () => blogsApi.getJobStatus(parseInt(id!)),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (
        data?.status === 'pending' ||
        data?.status === 'processing' ||
        data?.status?.startsWith('retrying')
      ) {
        return 3000;
      }
      return false;
    },
  });

  const queryClient = useQueryClient();
  const [showMediumModal, setShowMediumModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [publishStatus, setPublishStatus] = useState<MediumPublishStatus>('draft');

  const publishToMedium = useMutation({
    mutationFn: () =>
      blogsApi.publishToMedium(parseInt(id!), { publish_status: publishStatus }),
    onSuccess: (data) => {
      toast.success(`Blog published to Medium as ${data.medium_publish_status}!`);
      setShowMediumModal(false);
      queryClient.invalidateQueries({ queryKey: ['job', id] });
    },
    onError: (error: any) => {
      const message = error?.response?.data?.detail || 'Failed to publish to Medium';
      toast.error(message);
    },
  });

  const deleteBlog = useMutation({
    mutationFn: () => blogsApi.deleteBlog(parseInt(id!)),
    onSuccess: () => {
      toast.success('Blog deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      navigate('/blogs');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.detail || 'Failed to delete blog';
      toast.error(message);
    },
  });

  const status = job ? getStatusConfig(job.status) : null;

  const copyToClipboard = async (text: string, label: string) => {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  const downloadAsMarkdown = () => {
    if (!job?.result) return;
    const content = `# ${job.result.title}\n\n${job.result.content}`;
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${job.result.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.md`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Blog downloaded!');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <Spinner size="lg" />
          <p className="text-gray-500 dark:text-gray-400">Loading blog...</p>
        </div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md text-center p-8">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Blog not found
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            The blog you're looking for doesn't exist or has been deleted.
          </p>
          <Link to="/blogs">
            <Button variant="outline" leftIcon={<ArrowLeft className="h-4 w-4" />}>
              Back to Blogs
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Back Button */}
      <Link to="/blogs">
        <Button variant="ghost" leftIcon={<ArrowLeft className="h-4 w-4" />}>
          Back to Blogs
        </Button>
      </Link>

      {/* Status Banner for non-completed jobs */}
      {job.status !== 'completed' && status && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card
            className={`border-l-4 ${
              job.status === 'processing' ? 'border-l-blue-500' :
              job.status === 'pending' ? 'border-l-yellow-500' :
              job.status.startsWith('retrying') ? 'border-l-orange-400' :
              'border-l-red-500'
            }`}
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div
                  className={`p-3 rounded-full ${
                    job.status === 'processing' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600' :
                    job.status === 'pending' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600' :
                    job.status.startsWith('retrying') ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-600' :
                    'bg-red-100 dark:bg-red-900/30 text-red-600'
                  }`}
                >
                  {status.icon}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {status.label}
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    {status.message}
                  </p>
                  {job.error && (
                    <p className="text-red-500 mt-2 text-sm">{job.error}</p>
                  )}
                </div>
                {job.status === 'failed' && (
                  <Button variant="outline" onClick={() => navigate('/generate')}>
                    Try Again
                  </Button>
                )}
              </div>

              {(job.status === 'processing' || job.status === 'pending' || job.status.startsWith('retrying')) && (
                <div className="mt-6">
                  <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-gradient-to-r from-blue-500 to-purple-500"
                      initial={{ width: '0%' }}
                      animate={{ width: job.status === 'processing' ? '60%' : '20%' }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                  <div className="flex justify-between mt-2 text-sm text-gray-500 dark:text-gray-400">
                    <span>Started: {formatDateTime(job.created_at)}</span>
                    <span>Estimated: 2-5 minutes</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Blog Info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {job.status === 'completed' && job.result ? (
            <>
              {/* Tabs */}
              <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setActiveTab('content')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'content'
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  <FileText className="h-4 w-4 inline mr-2" />
                  Content
                </button>
                <button
                  onClick={() => setActiveTab('seo')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'seo'
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  <Search className="h-4 w-4 inline mr-2" />
                  SEO
                </button>
              </div>

              {activeTab === 'content' ? (
                <Card>
                  <CardContent className="p-8">
                    <article className="prose prose-lg dark:prose-invert max-w-none">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{job.result.content}</ReactMarkdown>
                    </article>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Meta Title</CardTitle>
                    </CardHeader>
                    <CardContent className="p-6 pt-0">
                      <div className="flex items-start justify-between gap-4">
                        <p className="text-gray-900 dark:text-white">
                          {job.result.meta_title || 'N/A'}
                        </p>
                        {job.result.meta_title && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(job.result!.meta_title, 'Meta title')}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-2">
                        {job.result.meta_title?.length || 0}/60 characters
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Meta Description</CardTitle>
                    </CardHeader>
                    <CardContent className="p-6 pt-0">
                      <div className="flex items-start justify-between gap-4">
                        <p className="text-gray-900 dark:text-white">
                          {job.result.meta_description || 'N/A'}
                        </p>
                        {job.result.meta_description && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(job.result!.meta_description, 'Meta description')}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-2">
                        {job.result.meta_description?.length || 0}/160 characters
                      </p>
                    </CardContent>
                  </Card>

                  {job.result.seo_suggestions && (
                    <Card>
                      <CardHeader>
                        <CardTitle>SEO Suggestions</CardTitle>
                      </CardHeader>
                      <CardContent className="p-6 pt-0">
                        <div className="prose prose-sm dark:prose-invert max-w-none">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{job.result.seo_suggestions}</ReactMarkdown>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}

            </>
          ) : (
            <Card>
              <CardContent className="p-8">
                <div className="text-center py-12">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 mb-4">
                    <FileText className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                    Content not available yet
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    {job.status === 'failed'
                      ? 'The generation failed. Please try again.'
                      : 'Please wait while your blog is being generated.'}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Blog Details</CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0 space-y-4">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Status</p>
                <Badge variant={status?.variant} icon={status?.icon}>
                  {status?.label}
                </Badge>
              </div>

              {job.approval_status && (
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Approval</p>
                  {job.approval_status === 'approved' ? (
                    <Badge variant="success" icon={<BadgeCheck className="h-3.5 w-3.5" />}>
                      Approved
                    </Badge>
                  ) : job.approval_status === 'rejected' ? (
                    <Badge variant="danger" icon={<XCircle className="h-3.5 w-3.5" />}>
                      Rejected
                    </Badge>
                  ) : (
                    <Badge variant="warning" icon={<Hourglass className="h-3.5 w-3.5" />}>
                      Pending Review
                    </Badge>
                  )}
                </div>
              )}

              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Title</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {job.result?.title || job.selected_title || 'Generating...'}
                </p>
              </div>

              {job.result && (
                <>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Word Count</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {job.result.word_count} words
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Reading Time</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {estimateReadingTime(job.result.word_count)}
                    </p>
                  </div>
                </>
              )}

              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Created</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {formatDateTime(job.created_at)}
                </p>
              </div>

              {job.result?.medium_url && (
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Medium</p>
                  <a
                    href={job.result.medium_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Published ({job.result.medium_publish_status})
                  </a>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Token Usage */}
          {job.token_usage && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-4 w-4 text-yellow-500" />
                  Token Usage
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 pt-0 space-y-4">
                {/* Total */}
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Total</p>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {job.token_usage.total_tokens.toLocaleString()}
                  </p>
                </div>

                {/* Input / Output columns */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg bg-sky-50 dark:bg-sky-900/20 p-3 text-center">
                    <p className="text-xs text-sky-600 dark:text-sky-400 mb-1">Input</p>
                    <p className="font-semibold text-sky-700 dark:text-sky-300 text-sm">
                      {job.token_usage.input_tokens.toLocaleString()}
                    </p>
                  </div>
                  <div className="rounded-lg bg-purple-50 dark:bg-purple-900/20 p-3 text-center">
                    <p className="text-xs text-purple-600 dark:text-purple-400 mb-1">Output</p>
                    <p className="font-semibold text-purple-700 dark:text-purple-300 text-sm">
                      {job.token_usage.output_tokens.toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* Node Breakdown */}
                {job.token_breakdown && job.token_breakdown.length > 0 && (() => {
                  const maxTokens = Math.max(...job.token_breakdown.map(b => b.total_tokens));
                  return (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        Node Breakdown
                      </p>
                      <div className="space-y-2">
                        {job.token_breakdown.map((item, idx) => {
                          const inputPct = (item.input_tokens / maxTokens) * 100;
                          const outputPct = (item.output_tokens / maxTokens) * 100;
                          const label = item.run > 1 ? `${item.node} ×${item.run}` : item.node;
                          return (
                            <div key={idx} className="space-y-1">
                              <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                                {label}
                              </p>
                              {/* Input bar */}
                              <div className="flex items-center gap-2">
                                <div className="w-16 h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                                  <div
                                    className="h-full rounded-full bg-sky-500"
                                    style={{ width: `${inputPct}%` }}
                                  />
                                </div>
                                <span className="text-xs text-sky-600 dark:text-sky-400 w-14 text-right">
                                  {item.input_tokens.toLocaleString()}
                                </span>
                              </div>
                              {/* Output bar (outlined) */}
                              <div className="flex items-center gap-2">
                                <div className="w-16 h-1.5 rounded-full border border-purple-400 dark:border-purple-500 overflow-hidden">
                                  <div
                                    className="h-full rounded-full bg-purple-200 dark:bg-purple-700"
                                    style={{ width: `${outputPct}%` }}
                                  />
                                </div>
                                <span className="text-xs text-purple-600 dark:text-purple-400 w-14 text-right">
                                  {item.output_tokens.toLocaleString()}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          {job.status === 'completed' && job.result && (
            <div className="flex flex-col gap-3">
              <Button
                variant="outline"
                leftIcon={<Copy className="h-4 w-4" />}
                onClick={() => copyToClipboard(job.result!.content, 'Blog content')}
                className="w-full"
              >
                Copy Content
              </Button>
              <Button
                variant="outline"
                leftIcon={<Download className="h-4 w-4" />}
                onClick={downloadAsMarkdown}
                className="w-full"
              >
                Download Markdown
              </Button>
              {job.result?.medium_url ? (
                <a
                  href={job.result.medium_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    variant="success"
                    leftIcon={<ExternalLink className="h-4 w-4" />}
                    className="w-full"
                  >
                    View on Medium
                  </Button>
                </a>
              ) : (
                <Button
                  variant="outline"
                  leftIcon={<Send className="h-4 w-4" />}
                  onClick={() => setShowMediumModal(true)}
                  className="w-full"
                >
                  Publish to Medium
                </Button>
              )}
            </div>
          )}

          {/* Delete */}
          <Button
            variant="danger"
            leftIcon={<Trash2 className="h-4 w-4" />}
            onClick={() => setShowDeleteModal(true)}
            className="w-full"
          >
            Delete Blog
          </Button>

          {/* Keywords */}
          {job.keywords && job.keywords.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Keywords</CardTitle>
              </CardHeader>
              <CardContent className="p-6 pt-0">
                <div className="flex flex-wrap gap-2">
                  {job.keywords.map((keyword) => (
                    <Badge key={keyword} variant="primary" size="sm">
                      {keyword}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Medium Publish Modal */}
      <Modal
        isOpen={showMediumModal}
        onClose={() => setShowMediumModal(false)}
        title="Publish to Medium"
        description="Choose how you want to publish this blog on Medium."
        size="sm"
      >
        <div className="space-y-4">
          <Select
            label="Publish Status"
            options={[
              { value: 'draft', label: 'Draft - Save as draft on Medium' },
              { value: 'public', label: 'Public - Publish immediately' },
              { value: 'unlisted', label: 'Unlisted - Accessible via link only' },
            ]}
            value={publishStatus}
            onChange={(e) => setPublishStatus(e.target.value as MediumPublishStatus)}
          />
          <div className="flex gap-3 justify-end pt-2">
            <Button variant="ghost" onClick={() => setShowMediumModal(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              leftIcon={<Send className="h-4 w-4" />}
              isLoading={publishToMedium.isPending}
              onClick={() => publishToMedium.mutate()}
            >
              Publish
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Blog"
        description={`Are you sure you want to delete "${job.result?.title || job.selected_title}"? This action cannot be undone.`}
        size="sm"
      >
        <div className="flex gap-3 justify-end pt-2">
          <Button variant="ghost" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            leftIcon={<Trash2 className="h-4 w-4" />}
            isLoading={deleteBlog.isPending}
            onClick={() => deleteBlog.mutate()}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
};
